import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import classNames from 'classnames';
import {DatePicker} from 'antd';
import 'dayjs/locale/he';
import locale from 'antd/es/date-picker/locale/he_IL';
import moment from 'moment';
import * as actions from '../_redux/userDetailsActions';
import ImageOkOrNot from './ImageOkOrNot';
import {copyToClipboard} from '../../../../utils';
import {getDropdownOptions} from '../../../../utils';
import {ProtectedComponent} from '../../_common/components/Permissions';
import {toAbsoluteUrl} from '../../../../_metronic/_helpers';
import {getWhatsAppLink} from '../../_common/utils/format';
import styles from '../styles.module.scss';

const Input = ({onSave, onCancel, value, ...rest}) => {
  const [text, setText] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef) {
      inputRef.current.focus();
    }
  }, []);

  const handleChange = event => setText(event.target.value);
  const handleBlur = () => {
    onSave(text);
  };
  const handleKeyUp = event => {
    if (event.key === 'Enter') {
      onSave(text);
    }
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <input
      ref={inputRef}
      className="text-primary userDetails-action-input"
      type="text"
      name="text"
      id=""
      onBlur={handleBlur}
      value={text}
      onChange={handleChange}
      onKeyUp={handleKeyUp}
      {...rest}
    />
  );
};

const CheckBox = ({onCancel, onSave}) => {
  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  const handleCheck = () => {
    onSave(true);
  };
  const handleUnCheck = () => {
    onSave(false);
  };
  const handleKeyUp = event => {
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <>
      <ImageOkOrNot checked={true} onClick={handleCheck} className={'ml-1'}/>
      <ImageOkOrNot checked={false} onClick={handleUnCheck} className={'ml-1'}/>
    </>
  );
};

const Dropdown = ({ddType, value, onSave, onCancel, ...rest}) => {
  const [selected, setSelected] = useState(null);
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#ffffff' : '#3699ff',
    }),
  };

  const options = useSelector(
    (state) => state.partners.partnersOptions,
    shallowEqual
  );

  const promiseOptions = (inputValue) => getDropdownOptions({ddType});

  const handleChange = newValue => setSelected(newValue);
  const handleBlur = () => {
    if (selected) {
      onSave(selected.value);
    } else {
      onCancel();
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleKeyUp = event => {
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    (ddType === 'affiliate_company_items')
      ?
      <Select
        placeholder={value.label}
        options={options}
        className={'w-100'}
        styles={customStyles}
        onChange={handleChange}
        onBlur={handleBlur}
        autoFocus
        isSearchable={false}
        {...rest}
      />
      :
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={promiseOptions}
        className={'w-100'}
        styles={customStyles}
        onChange={handleChange}
        onBlur={handleBlur}
        autoFocus
        isSearchable={false}
        {...rest}
      />
  );
};

const SelectDate = ({onSave, onCancel, value, ...rest}) => {
  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleKeyUp = event => {
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <DatePicker
      className="form-control d-flex"
      placeholder={''}
      onChange={(date) => {
        onSave(Date.parse(moment(date.$d).format('YYYY-MM-DD')));
      }}
      locale={locale}
      format={'DD-MM-YYYY'}
      suffixIcon={false}
      clearIcon={false}
      showToday={false}
      {...rest}
    />
  );
};

export const InfoItem = ({
                           id,
                           keyName,
                           translationKey,
                           value = '-',
                           address,
                           alignType = 'column',
                           containerCustomClassName,
                           labelCustomClassName,
                           valueCustomClassName,
                           allowCopy = false,
                           allowEdit = false,
                           ddType,
                           inputType = 'text',
                           clickableItem = false,
                         }) => {
  const dispatch = useDispatch();
  const {
    _id: userID,
    profile: {
      firstName: agentFirstName
    },
    email
  } = useSelector((state) => state.userDetails.user, shallowEqual);
  const [editMode, setEditMode] = useState(false);
  const openEditMode = () => setEditMode(true);
  const closeEditMode = () => setEditMode(false);

  const submitNewValue = value => {
    const paramValue = (keyName === 'street' || keyName === 'houseNum' || keyName === 'appartmentNum')
      ? {newValue: value, entireValue: getEntireValue(keyName, value)}
      : value;
    const data = {userID, paramName: keyName, paramValue, email};
    const dataNotificationsTimestamp = {
      userID,
      paramName: 'notificationsTimestamp',
      paramValue: Date.parse(moment().format('YYYY-MM-DD')),
      email
    };

    return keyName === 'receiveNotifications'
      ? Promise.resolve()
        .then(() => dispatch(actions.editUserData(data)))
        .then(() => dispatch(actions.editUserData(dataNotificationsTimestamp)))
      : Promise.resolve().then(() => dispatch(actions.editUserData(data)));
  };

  const saveAndClose = value => {
    submitNewValue(value)
      .then(() => dispatch(actions.fetchUser({userId: userID})))
      .then(closeEditMode);
  };

  const getEntireValue = (keyName, value) => {
    const {street, houseNum, appartmentNum} = address;
    switch (keyName) {
      case 'street':
        return `${value} ${houseNum}/${appartmentNum}`;
      case 'houseNum':
        return `${street} ${value}/${appartmentNum}`;
      case 'appartmentNum':
      default:
        return `${street} ${houseNum}/${value}`;
    }
  };

  const getAlignClasses = () => {
    switch (alignType) {
      case 'flex':
        return {
          containerClassName: 'd-flex align-items-center justify-content-between',
          columnClassName: 'd-flex flex-nowrap',
        };
      case 'column':
      default:
        return {
          containerClassName: '',
          columnClassName: '',
        };
    }
  };

  const getEditComponent = () => {
    const props = {value, onSave: saveAndClose, onCancel: closeEditMode};
    switch (inputType) {
      case 'bool':
        const boolProps = {...props};
        return <CheckBox {...boolProps}/>;
      case 'dropdown':
        const ddProps = {...props, ddType};
        return <Dropdown {...ddProps}/>;
      case 'date':
        const dateProps = {...props};
        return <SelectDate {...dateProps} />;
      case 'text':
      default:
        const defProps = {...props};
        return <Input {...defProps} />;
    }
  };

  const copyValueToClipboard = value => () => copyToClipboard(value);

  const linkToMail = `mailto:${value}`;
  const {containerClassName, columnClassName} = getAlignClasses();
  const valueClassName = editMode
    ? `${valueCustomClassName} label label-lg label-inline label-light-secondary flex-fill`
    : valueCustomClassName;
  const message = ['היי', agentFirstName].filter(Boolean).join(' ');
  const linkToWhatsapp = getWhatsAppLink(value, encodeURI(message));

  return (
    <>
      <div
        key={id}
        className={classNames(
          `${containerClassName} mb-2 ${containerCustomClassName || ''}`,
          styles.infoItemContainer
        )}
      >
        <div
          className={classNames(`d-flex font-weight-bolder font-size-sm mr-2 ${labelCustomClassName || ''}`, columnClassName, styles.infoItemLabel)}>
        <span>
          <FormattedMessage id={translationKey} defaultMessage={keyName}/>
        </span>
          <span>:</span>
          <div className={styles.infoItemIcon}>
            {allowCopy && (
              <svg width="16px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                <g stroke="none" fill="none">
                  <rect x="0" y="0" width="24" height="24"/>
                  <path
                    d="M15.9956071,6 L9,6 C7.34314575,6 6,7.34314575 6,9 L6,15.9956071 C4.70185442,15.9316381
                  4,15.1706419 4,13.8181818 L4,6.18181818 C4,4.76751186 4.76751186,4 6.18181818,4 L13.8181818,4
                  C15.1706419,4 15.9316381,4.70185442 15.9956071,6 Z"
                    fill="#3699ff" opacity="0.4"/>
                  <path
                    d="M10.1818182,8 L17.8181818,8 C19.2324881,8 20,8.76751186 20,10.1818182 L20,17.8181818
                  C20,19.2324881 19.2324881,20 17.8181818,20 L10.1818182,20 C8.76751186,20 8,19.2324881
                  8,17.8181818 L8,10.1818182 C8,8.76751186 8.76751186,8 10.1818182,8 Z"
                    fill="#3699ff"
                    class={styles.actionIcon}
                    onClick={copyValueToClipboard(value)}
                  />
                </g>
              </svg>
            )}
            {allowEdit && (
              <ProtectedComponent action="full" subject="userPage">
                <svg width="16px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                  <g stroke="none" fill="none">
                    <rect id="bound" x="0" y="0" width="24" height="24"/>
                    <path
                      className="ml-1 userDetails-action-icon"
                      d="M7.10343995,21.9419885 L6.71653855,8.03551821 C6.70507204,7.62337518 6.86375628,7.22468355
                    7.15529818,6.93314165 L10.2341093,3.85433055 C10.8198957,3.26854411 11.7696432,3.26854411
                    12.3554296,3.85433055 L15.4614112,6.9603121 C15.7369117,7.23581259 15.8944065,7.6076995
                    15.9005637,7.99726737 L16.1199293,21.8765672 C16.1330212,22.7048909 15.4721452,23.3869929
                    14.6438216,23.4000848 C14.6359205,23.4002097 14.6280187,23.4002721 14.6201167,23.4002721
                    L8.60285976,23.4002721 C7.79067946,23.4002721 7.12602744,22.7538546 7.10343995,21.9419885 Z"
                      fill="#3699ff"
                      class={styles.actionIcon}
                      onClick={openEditMode}
                      transform="translate(11.418039, 13.407631) rotate(-135.000000) translate(-11.418039, -13.407631) "
                    />
                  </g>
                </svg>
              </ProtectedComponent>
            )}
          </div>
        </div>
        <div
          className={classNames(`${columnClassName} text-hover-primary font-size-md ${editMode && 'w-100'}`, styles.infoItemValue)}>
          {keyName === 'phoneNumber' && (
            <a href={linkToWhatsapp} target={'_blank'}
               className={styles.whatsappButton}
               title={'קישור לוואטסאפ'}
            >
              <img
                src={toAbsoluteUrl('/media/svg/Whatsapp_white.svg')}
                alt="whatsapp"
              />
            </a>
          )}
          <span className={classNames(
            `${valueClassName || ''} ${(editMode && (inputType === 'dropdown')) ? 'w-100' : ''}`,
            styles.infoItemField
          )}>
          {editMode
            ? getEditComponent()
            : clickableItem
              ? <a href={linkToMail}>{value}</a>
              : value
          }
        </span>
        </div>
      </div>
    </>
  );
};

InfoItem.propTypes = {
  id: PropTypes.any,
  keyName: PropTypes.any,
  translationKey: PropTypes.string,
  value: PropTypes.any,
  entireValue: PropTypes.any,
  alignType: PropTypes.oneOf(['flex', 'column']),
  containerCustomClassName: PropTypes.string,
  labelCustomClassName: PropTypes.string,
  valueCustomClassName: PropTypes.string,
  allowCopy: PropTypes.bool,
  allowEdit: PropTypes.bool,
  ddType: PropTypes.string,
  inputType: PropTypes.oneOf(['text', 'bool', 'dropdown', 'date']),
};

